import { EventEmitter, Injectable } from '@angular/core';
import { ProjectKeyValEntry } from '@shared/ProjectKeyValStore';
import { waitSeconds } from '@reflact/tsutil';
import dayjs from 'dayjs';
import { EventManagerAdminService } from './event-manager/event-manager-admin.service';
import { SocketService } from './socket-admin.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectKeyValAdminService {
  public onKeyValUpdate: EventEmitter<ProjectKeyValEntry> = new EventEmitter<ProjectKeyValEntry>();
  public onKeyValDelete: EventEmitter<ProjectKeyValEntry> = new EventEmitter<ProjectKeyValEntry>();

  public keyValMap = new Map<string, ProjectKeyValEntry>();

  public initDone = false;

  constructor(
    private socketService: SocketService,
    private eventManager: EventManagerAdminService
  ) {
    void this.init();
  }

  private getMapKey(key: string, type: string) {
    return `${type}-${key}`;
  }
  private getMapKeyByEntry(entry: ProjectKeyValEntry) {
    return this.getMapKey(entry.key, entry.type);
  }

  public async init() {
    const io = await this.socketService.getIo();
    io.on('projectKeyValUpdate', (entry: ProjectKeyValEntry) => {
      this.onKeyValUpdate.emit(entry);
      this.keyValMap.set(this.getMapKeyByEntry(entry), entry);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.remoteUpdate", entry);
    });
    io.on('projectKeyValDelete', (entry: ProjectKeyValEntry) => {
      this.onKeyValDelete.emit(entry);
      this.keyValMap.delete(this.getMapKeyByEntry(entry));
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.remoteDelete", entry);
    });
    const items = await this.getInitialValues();
    for (const item of items) {
      this.keyValMap.set(this.getMapKeyByEntry(item), item);
    }
    this.initDone = true;
  }

  public async waitForInit() {
    if (this.initDone) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.waitForInit", 'ProjectKeyVal Init done');
      return;
    }
    await waitSeconds(0.1);
    await this.waitForInit();
    return null;
  }

  public async getInitialValues() {
    const io = await this.socketService.getIo();
    return new Promise<ProjectKeyValEntry[]>((resolve, _reject) => {
      io.emit('getInitialProjectKeyVals', (entries: ProjectKeyValEntry[]) => {
        resolve(entries);
      });
    });
  }

  public async deleteKeyValEntry(key: string, type: string = "custom") {
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('removeProjectKeyVal', { key: key, type }, (data: ProjectKeyValEntry) => {
        this.keyValMap.delete(this.getMapKey(key, type));
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.deleteKeyValEntry", data);
        resolve(data);
      });
    });
  }

  public async storeKeyValEntry(key: string, value: unknown, type: string = "custom") {
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('storeProjectKeyValUpdate', { key: key, value: value, type }, (data: ProjectKeyValEntry) => {
        this.keyValMap.set(this.getMapKey(key, type), data);
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.storeKeyValEntry", data);
        resolve(data);
      });
    });
  }

  public async getKeyValEntry(key: string, type: string = "custom") {
    await this.waitForInit();
    const found = this.keyValMap.get(this.getMapKey(key, type));
    if (found) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.getKeyValEntry", { key, value: found.value });
      return found.value;
    } else {
      return undefined;
    }
  }

  public async getKeyValByType(type: string) {
    await this.waitForInit();
    const results: ProjectKeyValEntry[] = [];
    this.keyValMap.forEach((val, _key) => {
      if (val.type === type) {
        results.push(val);
      }
    });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.getKeyValByType", results);
    return results;
  }

  public async get(key: string, type: string = "custom") {
    const val = (await this.getByType(type))
      .find(obj => obj.key === key)
      ?.value ?? null;
    return val;
  }

  public async set(key: string, value: unknown, type: string = "custom") { return await this.storeKeyValEntry(key, value, type); }
  public async unique(key: string, value: unknown, type: string = "custom") { return await this.storeKeyValEntry(key + '_' + dayjs().toISOString(), value, type); }
  public async getByType(type: string) { return this.getKeyValByType(type); }
}
